interface TableData {
    headers: string[];
    rows: string[][];
}

function stripEmojis(text: string): string {
    return text
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/[\u{FE00}-\u{FE0F}\u{200D}\u{20E3}]/gu, '')
        .trim();
}

function parseTable(tableEl: HTMLTableElement): TableData {
    const headers: string[] = [];
    tableEl.querySelectorAll('thead th').forEach((th) => {
        headers.push(stripEmojis(th.textContent || ''));
    });

    const rows: string[][] = [];
    tableEl.querySelectorAll('tbody tr').forEach((tr) => {
        const row: string[] = [];
        tr.querySelectorAll('td').forEach((td) => {
            row.push(stripEmojis(td.textContent || ''));
        });
        rows.push(row);
    });

    return { headers, rows };
}

function escapeCsvField(field: string): string {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

export function getPrecedingHeadingText(
    el: HTMLElement | null
): string | undefined {
    let sibling = el?.previousElementSibling;
    while (sibling) {
        if (/^H[1-6]$/.test(sibling.tagName)) {
            return stripEmojis(sibling.textContent || '') || undefined;
        }
        sibling = sibling.previousElementSibling;
    }
    return undefined;
}

export function exportTableToCsv(tableEl: HTMLTableElement, title?: string) {
    const { headers, rows } = parseTable(tableEl);

    const csvLines = [
        headers.map(escapeCsvField).join(','),
        ...rows.map((row) => row.map(escapeCsvField).join(','))
    ];
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvLines.join('\n')], {
        type: 'text/csv;charset=utf-8;'
    });
    downloadBlob(blob, `${title || 'table-export'}.csv`);
}

export async function exportTableToPdf(
    tableEl: HTMLTableElement,
    title?: string
) {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const { headers, rows } = parseTable(tableEl);

    const isLandscape = headers.length > 5;
    const doc = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait'
    });

    let startY = 20;
    if (title) {
        doc.setFontSize(16);
        doc.setTextColor(40, 40, 40);
        doc.text(title, 14, startY);
        startY += 10;
    }

    autoTable(doc, {
        head: [headers],
        body: rows,
        startY,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: {
            fillColor: [245, 245, 245],
            textColor: [40, 40, 40],
            fontStyle: 'bold',
            lineWidth: { bottom: 0.5 },
            lineColor: [200, 200, 200]
        },
        bodyStyles: {
            fillColor: [255, 255, 255],
            lineWidth: { bottom: 0.25 },
            lineColor: [220, 220, 220]
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255]
        }
    });

    doc.save(`${title || 'table-export'}.pdf`);
}
